import { StoryblokComponent } from '@storyblok/react';

import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useGlobalProviderContext } from '../../../context/global';
import { JsonObject } from '../../../types/jsonObject';
import {
  ServiceOSApiClient,
  ServiceOSRegisterRequestPayload,
} from '../../../utils/serviceOSApiClient';
import { FormBuilderRequestLifecycleContext } from '../../FormBuilder/FormBuilder.types';
import { FormBuilderDefaultFieldValue } from '../../FormBuilder/hooks/useFormBuilderState';
import { ProSignUpFormProps } from './ProSignUpForm.types';
import { POST_SIGN_UP_SERVICEOS_EMAIL_QUERY_PARAM } from './ProSignUpSuccessMessageBlock/ProSignUpSuccessMessageBlock';

const REFERRER_CODE_QUERY_PARAM = 'referrer_code';

const serviceOSApiClient = new ServiceOSApiClient();

export const ProSignUpForm = (props: ProSignUpFormProps) => {
  const { blok } = props;
  const { form } = blok;

  const globalContext = useGlobalProviderContext();
  const router = useRouter();

  const defaultFieldValues = useMemo<FormBuilderDefaultFieldValue[]>(() => {
    const referrerCode = router.query[REFERRER_CODE_QUERY_PARAM];
    if (!referrerCode) return [];

    const code = Array.isArray(referrerCode) ? referrerCode[0] : referrerCode;
    return [
      {
        key: REFERRER_CODE_QUERY_PARAM,
        value: code,
        includeInSubmitRequest: true,
      },
    ];
  }, [router.query]);

  if (!form.length) {
    return null;
  }

  const handleSubmit = async (data: JsonObject, context: FormBuilderRequestLifecycleContext) => {
    const payload: ServiceOSRegisterRequestPayload = {
      first_name: data.first_name as string,
      last_name: data.last_name as string,
      email: data.email as string,
      password: data.password as string,
      referrer_code: data.referrer_code as string,
      ...(data.allow_mk_all && {
        preferences: {
          allow_mk_all: 1,
        },
      }),
    };
    const { successMessage, errorMessage } = await serviceOSApiClient.register(
      payload,
      router.locale
    );

    if (errorMessage) {
      context.setErrorMessage(errorMessage);
    } else if (successMessage) {
      context.setSuccessMessage(successMessage);

      if (globalContext.serviceOSSettings.postSignUpPageUrl) {
        const url = new URL(
          globalContext.serviceOSSettings.postSignUpPageUrl,
          window.location.origin
        );
        url.searchParams.set(POST_SIGN_UP_SERVICEOS_EMAIL_QUERY_PARAM, data.email as string);

        void router.push(url);
      }
    }
  };

  const formBuilderBlok = {
    ...form[0],
    submitDataAs: 'json',
  };

  return (
    <StoryblokComponent
      blok={formBuilderBlok}
      key={formBuilderBlok._uid}
      onSubmit={handleSubmit}
      defaultFieldValues={defaultFieldValues}
    />
  );
};
